import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import CurrentPrice from '@gcc/pip-components/dist/templates/Price/CurrentPrice';
import config from '../helpers/config';
import * as designBuilderApi from '../api/designBuilder';
import { createAndSendPageLoadEvent } from '../analytics/commonAnalytics';
import AnalyticsConstants from '../analytics/constants';
import ItemError from '../components/EstimateCard/ContainerCards/ItemCards/ItemError';

const DesignSummaryList = ({
  getDesignsSummary,
  cartEstimate,
  match,
  designs,
  phoneNumber,
}) => {
  useEffect(() => {
    // eslint-disable-next-line prefer-destructuring
    const designIds = match.params.designIds;
    getDesignsSummary(designIds);

    createAndSendPageLoadEvent(
      window.globalConfig.siteId,
      AnalyticsConstants.PageNames.DesignSummary,
      AnalyticsConstants.PageTypes.InterconnectedQuotes,
      AnalyticsConstants.PrimaryCategories.InterconnectedQuotes,
      AnalyticsConstants.PageNames.DesignSummary,
      AnalyticsConstants.PageNames.DesignSummary,
    );
  }, []);

  const getPirateCartUrl = designId => `${config.customBaseUrl}/window-treatments/estimate/${designId}`;

  const getMandiDesignUrl = designId => `${config.estimatebuilderBasePaths.mandi}${config.estimatebuilderLoadPath}/${designId}`;

  const getEstimateBuilderUrl = designId => `${config.estimatebuilderBasePath}${config.estimatebuilderLoadPath}/${designId}`;

  const hasInstall = designType => designType === 'With Professional Installation';

  const useMandiStoreToOnlineEnabled = () => {
    const { useMandiStoreToOnline } = queryString.parse(window.location.search);
    return useMandiStoreToOnline
      ? useMandiStoreToOnline.toLowerCase() === 'true' || config.useMandiStoreToOnline.toLowerCase() === 'true'
      : config.useMandiStoreToOnline.toLowerCase() === 'true';
  };

  const getMandiOrPirateCartUrl = (design) => {
    let url;

    if (design.isMandi) {
      url = getMandiDesignUrl(design.designId);
      return url;
    }

    if (useMandiStoreToOnlineEnabled() && hasInstall(design.designType)) {
      url = getPirateCartUrl(design.designId);
      return url;
    }

    return url;
  };

  const addToCartBtnClicked = (design) => {
    const url = getMandiOrPirateCartUrl(design);

    if (url) {
      window.location = url;
    }

    designBuilderApi
      .getItemDataForAnalytics(design.designId)
      .then((response) => {
        cartEstimate(response.data.items, design.designId);
      });
  };

  const getUrlForDesignLink = (design) => {
    const designLinkUrl = getMandiOrPirateCartUrl(design);

    return designLinkUrl || getEstimateBuilderUrl(design.designId);
  };


  const renderErrors = (design) => {
    if (design.hasDiscontinuedProduct) {
      return (
        <div className="singleRow">
          <ItemError message={`One or more products in the design below is discontinued.
          Review now or contact <a href='tel:${phoneNumber}'>${phoneNumber}</a> for help. `}
          />
        </div>
      );
    }
    if (!design.isDesignValid) {
      return (
        <div className="singleRow">
          <ItemError message={`One or more products in the design below requires edits before purchase.
          Edit now or contact <a href='tel:${phoneNumber}'>${phoneNumber}</a> for help. `}
          />
        </div>
      );
    }

    return null;
  };

  const shouldAddToCartButtonBeDisabled = design =>
    !design.isDesignValid || design.hasDiscontinuedProduct;

  return (
    <div className="designSummaryList">
      <div className="title">My Designs</div>
      <div className="headerLine" />
      <div className="summaryTable">
        <div className="headerRow">
          <div className="tableHeader bolded alignInitial">Design Name</div>
          {useMandiStoreToOnlineEnabled() && <div className="tableHeader bolded">Design Type</div>}
          <div className="tableHeader bolded">Date Created</div>
          <div className="tableHeader bolded">Quote ID</div>
          <div className="tableHeader bolded">Qty</div>
          <div className="tableHeader bolded">Subtotal</div>
          <div className="tableHeader">&nbsp;</div>
        </div>
        {designs.map(design => (
          <div key={`id:${design.designId}`}>
            {renderErrors(design)}
            <div className="summaryTableRow">
              <div className="tableHeaderMobile bolded">Design Name</div>
              <div className="tableData alignInitial">
                <span className="designName">
                  <a
                    className="designLink"
                    href={getUrlForDesignLink(design)}
                  >
                    {design.designName}
                  </a>
                </span>
              </div>
              {useMandiStoreToOnlineEnabled() && <div className="tableHeaderMobile bolded">Design Type</div>}
              {useMandiStoreToOnlineEnabled() && <div className="tableData">{design.designType}</div>}
              <div className="tableHeaderMobile bolded">Date Created</div>
              <div className="tableData">{new Date(`${design.createdDate}`).toLocaleDateString()}</div>
              <div className="tableHeaderMobile bolded">Quote ID</div>
              <div className="tableData">{design.orderNumber.toUpperCase()}</div>
              <div className="tableHeaderMobile bolded">Qty</div>
              <div className="tableData bolded">{design.quantity}</div>
              <div className="tableHeaderMobile bolded">Subtotal</div>
              <div className="tableData bolded">
                <CurrentPrice price={design.subtotal} />
              </div>
              <div className="tableData">
                <button
                  className="addToCartButton btn-primary-base"
                  disabled={shouldAddToCartButtonBeDisabled(design)}
                  onClick={() => addToCartBtnClicked(design)}
                >
                  Add To Cart
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

DesignSummaryList.propTypes = {
  getDesignsSummary: PropTypes.func.isRequired,
  cartEstimate: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired, // eslint-disable-line
  designs: PropTypes.arrayOf(PropTypes.object),
  phoneNumber: PropTypes.string.isRequired,
};

DesignSummaryList.defaultProps = {
  designs: [],
};


export default DesignSummaryList;
